// src/features/staff/services/staffApi.ts (EXTENDED)

import apiClient from '@/lib/api-client';

// ============================================================
// TYPES
// ============================================================

export interface StaffMember {
    id: string;
    tenantId: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string | null;
    avatarUrl: string | null;
    employeeId: string | null;
    posAccess: 'none' | 'retail' | 'bom' | 'both';
    isActive: boolean;
    posPin: string | null;
    lastLoginAt: string | null;
    roles: { id: string; name: string }[];
    businessName?: string;
    branchName?: string;
    businessId?: string;
    branchId?: string;
    createdAt: string;
    updatedAt: string;
}

export interface StaffListResponse {
    data: StaffMember[];
    meta: { totalCount: number; hasNextPage: boolean };
}

export interface StaffPermission {
    userId: string;
    roles: Array<{
        id: string; name: string;
        permissions: Array<{ id: string; code: string; resource: string; action: string }>;
    }>;
    effectivePermissions: Array<{ code: string; resource: string; action: string }>;
    overrides: Array<{ id: string; module: string; action: string; granted: boolean; reason: string | null }>;
}

export interface StaffActivity {
    id: string; userId: string; action: string;
    entityType: string; entityId: string | null;
    oldValues: Record<string, unknown> | null;
    newValues: Record<string, unknown> | null;
    createdAt: string;
}

export interface StaffShift {
    id: string; branchName?: string;
    openingCash: string; countedCash: string | null;
    cashVariance: string | null; totalSales: string;
    transactionCount: number; status: string;
    openedAt: string; closedAt: string | null;
}

export interface StaffSalesSummary {
    totalSales: string;
    totalTransactions: number;
    averageBasket: string;
    totalDiscounts: string;
    totalVoids: number;
    totalRefunds: number;
    topProducts: { productId: string; productName: string; quantity: number; revenue: string }[];
    dailySales: { date: string; total: string; transactions: number }[];
}

export interface StaffPerformanceMetrics {
    staffId: string;
    staffName: string;
    role: string;
    branchName: string;
    totalSales: string;
    totalTransactions: number;
    averageBasket: string;
    totalItems: number;
    totalShifts: number;
    totalHours: number;
    salesPerHour: string;
    rank: number;
}

export interface PerformanceResponse {
    performance: StaffPerformanceMetrics[];
    summary: {
        totalStaff: number;
        totalSales: string;
        totalTransactions: number;
        topPerformer: string;
        topPerformerSales: string;
    };
}

// ============================================================
// STAFF CRUD
// ============================================================

export async function getStaff(tenantId: string, params?: {
    limit?: number;
    cursor?: string;  // ✅ Use cursor, not page
    search?: string;
    roleId?: string;
    isActive?: boolean;
    branchId?: string;
    sortBy?: string;
    order?: string;
}) {
    // ✅ Filter out undefined values and ensure limit ≤ 50
    const cleanParams: Record<string, any> = {};

    if (params?.limit) {
        cleanParams.limit = Math.min(params.limit, 50); // Max 50
    } else {
        cleanParams.limit = 50; // Fetch max allowed
    }

    if (params?.cursor) cleanParams.cursor = params.cursor;
    if (params?.search) cleanParams.search = params.search;
    if (params?.roleId) cleanParams.roleId = params.roleId;
    if (params?.isActive !== undefined) cleanParams.isActive = params.isActive;
    if (params?.branchId) cleanParams.branchId = params.branchId;
    if (params?.sortBy) cleanParams.sortBy = params.sortBy;
    if (params?.order) cleanParams.order = params.order;

    const response = await apiClient.get(`/tenants/${tenantId}/users`, { params: cleanParams });
    return response.data;
}


export async function getStaffById(tenantId: string, userId: string): Promise<StaffMember> {
    const response = await apiClient.get(`/tenants/${tenantId}/users/${userId}`);
    return response.data?.data;
}

export async function createStaff(tenantId: string, data: {
    firstName: string; lastName: string; email: string; phone?: string;
    password: string; roleIds: string[];
    employeeId?: string;  // ✅ Add this
    branchIds?: string[];  // ✅ Changed from branchId to branchIds (array)
    posAccess?: 'none' | 'retail' | 'bom' | 'both'; // ✅ ADD THIS
}) {
    const response = await apiClient.post(`/tenants/${tenantId}/users`, data);
    return response.data?.data;
}

export async function updateStaff(tenantId: string, userId: string, data: Record<string, unknown>) {
    const response = await apiClient.patch(`/tenants/${tenantId}/users/${userId}`, data);
    return response.data?.data;
}

export async function deactivateStaff(tenantId: string, userId: string) {
    const response = await apiClient.patch(`/tenants/${tenantId}/users/${userId}`, { isActive: false });
    return response.data?.data;
}

export async function reactivateStaff(tenantId: string, userId: string) {
    const response = await apiClient.patch(`/tenants/${tenantId}/users/${userId}`, { isActive: true });
    return response.data?.data;
}

export async function resetStaffPassword(tenantId: string, userId: string, newPassword: string) {
    const response = await apiClient.post(`/tenants/${tenantId}/users/${userId}/reset-password`, { newPassword });
    return response.data?.data;
}

export async function setStaffPin(tenantId: string, userId: string, pin: string) {
    const response = await apiClient.post(`/tenants/${tenantId}/users/${userId}/set-pin`, { pin });
    return response.data?.data;
}

// ============================================================
// STAFF ASSIGNMENTS
// ============================================================

export async function assignStaffToBusiness(tenantId: string, userId: string, businessId: string, scope: 'all_branches' | 'specific_branch' = 'all_branches') {
    const response = await apiClient.post(`/tenants/${tenantId}/users/${userId}/assign-business`, { businessId, scope });
    return response.data?.data;
}

export async function assignStaffToBranch(tenantId: string, userId: string, branchId: string) {
    const response = await apiClient.post(`/tenants/${tenantId}/users/${userId}/assign-branch`, { branchId });
    return response.data?.data;
}

export async function getStaffBusinesses(tenantId: string, userId: string) {
    const response = await apiClient.get(`/tenants/${tenantId}/users/${userId}/businesses`);
    return response.data?.data || [];
}

export async function getStaffBranches(tenantId: string, userId: string) {
    const response = await apiClient.get(`/tenants/${tenantId}/users/${userId}/branches`);
    return response.data?.data || [];
}

// ============================================================
// STAFF DETAILS
// ============================================================

export async function getStaffPermissions(tenantId: string, userId: string): Promise<StaffPermission> {
    const response = await apiClient.get(`/tenants/${tenantId}/users/${userId}/permissions`);
    return response.data?.data;
}

export async function getStaffActivity(tenantId: string, userId: string, limit = 50): Promise<StaffActivity[]> {
    const response = await apiClient.get(`/tenants/${tenantId}/users/${userId}/activity`, { params: { limit } });
    return response.data?.data || [];
}

export async function getStaffShifts(tenantId: string, userId: string, limit = 30): Promise<StaffShift[]> {
    const response = await apiClient.get(`/tenants/${tenantId}/users/${userId}/shifts`, { params: { limit } });
    return response.data?.data || [];
}

export async function getStaffSales(tenantId: string, userId: string, params?: { startDate?: string; endDate?: string }): Promise<StaffSalesSummary> {
    const response = await apiClient.get(`/tenants/${tenantId}/users/${userId}/sales`, { params });
    return response.data?.data;
}

// ============================================================
// STAFF PERFORMANCE
// ============================================================

export async function getStaffPerformance(tenantId: string, params?: {
    startDate?: string; endDate?: string; businessId?: string; branchId?: string;
    sortBy?: string; order?: string; limit?: number;
}): Promise<PerformanceResponse> {
    const response = await apiClient.get(`/tenants/${tenantId}/users/performance`, { params });
    return response.data?.data;
}

// ============================================================
// ROLES (for staff assignment)
// ============================================================

export async function getRoles(tenantId: string) {
    const response = await apiClient.get(`/tenants/${tenantId}/roles`);
    return response.data?.data || [];
}